import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  HeadContent,
  Link,
  Outlet,
  Scripts,
  createRootRouteWithContext,
  useRouter,
} from "@tanstack/react-router";
import { startTransition, useEffect, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Toaster } from "@/components/ui/sonner";
import { setLanguage, SUPPORTED_LANGS } from "@/i18n";
import { supabase } from "@/integrations/supabase/client";
import { reportLovableError } from "../lib/lovable-error-reporting";
import "../styles.css";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="font-serif text-7xl">404</h1>
        <h2 className="mt-4 text-xl font-medium">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">This route doesn't exist.</p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-full bg-foreground px-5 py-2.5 text-sm font-medium text-background hover:opacity-90"
          >
            Return home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center hairline rounded-2xl bg-card p-8">
        <h1 className="font-serif text-3xl">Something went wrong</h1>
        <p className="mt-2 text-sm text-muted-foreground">Please try again or return home.</p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="rounded-full bg-foreground px-4 py-2 text-sm font-medium text-background"
          >
            Try again
          </button>
          <a href="/" className="rounded-full hairline px-4 py-2 text-sm">
            Home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      {
        name: "keywords",
        content:
          "AI twin, digital twin, AI agents for business, conversational AI platform, lead qualification AI, ruznic marketing, hinwil, switzerland",
      },
      { name: "author", content: "Ruznic Marketing" },
      { name: "theme-color", content: "#FAFAF7" },
      { name: "google-site-verification", content: "mh0KofxWQ8__WwOWJzyXV5DFV9EwN07dklBpUORDEBQ" },
    ],

    links: [],

    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Ruznic Marketing",
          legalName: "Ruznic Marketing",
          founder: { "@type": "Person", name: "Ajdin Ruznic" },
          // Ruznic Marketing is a Swiss sole proprietorship (Einzelunternehmen)
          // and RealityTwin.io is one of its projects. No commercial-register
          // identifier is published because a sole proprietorship is only
          // required to register above CHF 100'000 turnover — stating a number
          // we don't have would be worse than omitting it.
          url: "https://ruznic-marketing.com",
          description:
            "Ruznic Marketing is a Swiss marketing studio building Reality Twin — the verified company layer for the AI-agent web. Currently publicly available.",
          address: {
            "@type": "PostalAddress",
            streetAddress: "Leimgrubstrasse 5",
            postalCode: "8340",
            addressLocality: "Hinwil",
            addressCountry: "CH",
          },
          areaServed: ["CH", "EU"],
          sameAs: ["https://realitytwin.io", "https://ruznic-marketing.com"],
          brand: { "@type": "Brand", name: "Reality Twin" },
        }),
      },
      {
        // WebSite entity: lets Google understand the site as a named property
        // and is the prerequisite for a sitelinks search box. Without it the
        // brand name in results is inferred rather than declared.
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "@id": "https://realitytwin.io/#website",
          name: "Reality Twin",
          alternateName: "Reality Twin Network",
          url: "https://realitytwin.io",
          inLanguage: "en",
          description:
            "Turn your company into an AI Twin that answers, qualifies and is reachable by other companies' agents — with verified identity and sources behind every answer.",
          publisher: { "@type": "Organization", name: "Ruznic Marketing" },
          potentialAction: {
            "@type": "SearchAction",
            target: {
              "@type": "EntryPoint",
              urlTemplate: "https://realitytwin.io/marketplace?q={search_term_string}",
            },
            "query-input": "required name=search_term_string",
          },
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const router = useRouter();
  const { i18n } = useTranslation();
  useEffect(() => {
    // 1) An explicit choice always wins: a saved preference (rt_lang) or a
    //    ?lang= override in the URL. 2) Otherwise, first-time visitors get the
    //    browser's preferred language automatically — without this a German or
    //    Spanish visitor would land on English and have to hunt for the switcher.
    const explicit =
      window.localStorage.getItem("rt_lang") ||
      new URLSearchParams(window.location.search).get("lang");
    const detectFromBrowser = (): string | null => {
      const prefs =
        typeof navigator !== "undefined" && navigator.languages?.length
          ? navigator.languages
          : [typeof navigator !== "undefined" ? navigator.language : ""];
      for (const p of prefs) {
        const base = (p || "").split("-")[0].toLowerCase();
        if ((SUPPORTED_LANGS as readonly string[]).includes(base)) return base;
      }
      return null;
    };
    const target = explicit || detectFromBrowser();
    if (target) {
      const base = target.split("-")[0];
      // Keep a cookie in sync so the server can render localized content on the
      // next request (SSR) instead of English-then-swap.
      document.cookie = `rt_lang=${base}; path=/; max-age=31536000; SameSite=Lax`;
      if (base !== i18n.language) {
        window.requestAnimationFrame(() => {
          startTransition(() => {
            void setLanguage(target);
          });
        });
      }
    }
    const apply = (lng: string) => {
      const base = (lng || "en").split("-")[0];
      if (typeof document !== "undefined") document.documentElement.lang = base;
    };
    apply(i18n.resolvedLanguage || i18n.language);
    i18n.on("languageChanged", apply);
    return () => i18n.off("languageChanged", apply);
  }, [i18n]);
  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event !== "SIGNED_IN" && event !== "SIGNED_OUT" && event !== "USER_UPDATED") return;
      router.invalidate();
      if (event !== "SIGNED_OUT") queryClient.invalidateQueries();
    });
    return () => sub.subscription.unsubscribe();
  }, [router, queryClient]);
  return (
    <QueryClientProvider client={queryClient}>
      <Outlet />
      <Toaster theme="light" />
    </QueryClientProvider>
  );
}
